import React, { useContext, useEffect, memo, useRef } from "react";
import { useDispatch } from "react-redux";
import { Col, Row } from "antd";
import { updateTitle } from "@features/titleSlice";
import { campaignCreationContext } from "@ts/constants";
import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel";
import StepsComponent from "./StepsComponent";
import { CampaignCreationModal } from "./CampaignCreationModal";
import { getProductGroups, getClusterGroups, formatClusterDataWithProducts, getCitiesList } from "@/ts/retrieve-data";
import { mapProductGroupToUIFormat } from "@/ts/utils";
import { setClusters } from "@/features/clustersSlice";
import {  IClusterFormData, IProductGroup } from "@/ts/common-types";

const CampaignCreationContent: React.FC = memo(() => {
    const {
        clusters,
        currentStep,
        setFilteredClusters,
        isModalVisible,
        setProductGroups,
        setFilteredProducts,
        setIsLoadingProducts,
        activeStep,
        setCitiesList,
        modalType,
    } = useContext(campaignCreationContext);
    const dispatch = useDispatch();
    const tableContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        dispatch(updateTitle("Campaigns Creation Wizard"));

        // Fetch both product groups and clusters
        const fetchData = async () => {
            setIsLoadingProducts(true);
            try {
                const productGroupsResponse = await getProductGroups();
                if (productGroupsResponse && productGroupsResponse?.length > 0) {
                    const mappedProducts: IProductGroup[] = productGroupsResponse.map(
                        mapProductGroupToUIFormat
                    );
                    setProductGroups(mappedProducts);
                    setFilteredProducts(mappedProducts);
                    const clustersData = await getClusterGroups();
                    if (clustersData) {
                        const formattedClusters = formatClusterDataWithProducts(clustersData, productGroupsResponse);
                        dispatch(setClusters(formattedClusters));
                    }
                }
                const citiesList = await getCitiesList();
                if (citiesList) {
                    setCitiesList(citiesList);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoadingProducts(false);
            }
        };

        fetchData();
    }, [dispatch, setFilteredProducts, setIsLoadingProducts, setProductGroups]);

    // Initialize filtered clusters with real clusters - filter out product clusters
    useEffect(() => {
        const realClusters = clusters.filter((cluster: IClusterFormData) => !cluster.id.startsWith('##productCluster_'));
        setFilteredClusters(realClusters);
    }, [clusters, setFilteredClusters]);

    return (
        <>
            <StepsComponent />
            <div
                className="panels-container"
                style={{
                    height: "calc(100vh - 186px)",
                    overflow: "hidden",
                }}
            >
                <Row gutter={0} style={{ height: "100%" }}>
                    {currentStep === 0 && (
                        <Col
                            flex="460px"
                            style={{
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            <LeftPanel />
                        </Col>
                    )}
                    {activeStep?.accordionType && (
                        <Col
                            flex="calc(100% - 460px)"
                            style={{
                                height: "100%",
                                overflow: "hidden",
                            }}
                        >
                            <RightPanel />
                        </Col>
                    )}
                </Row>
            </div>
            {isModalVisible && (
                <CampaignCreationModal
                    modalType={modalType}
                    tableContainerRef={tableContainerRef}
                    isVisible={isModalVisible}
                />
            )}
        </>
    );
});

export default CampaignCreationContent;
